import { createContext, createElement, useState, useEffect, useCallback, useContext } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, IS_FIREBASE_ENABLED } from '../lib/firebase'
import {
  getSession,
  clearSession,
  getAnonymousSession,
  getAnonymousToken,
  clearAnonymousSession,
  type DemoStudent,
} from '../lib/auth'
import { api, OfflineError } from '../lib/api'
import { setSentryUser } from '../lib/sentry'
import { identify as identifyAnalytics, track } from '../lib/analytics'
import { identifyClarity } from '../lib/clarity'
import {
  readAuthCache,
  readLatestAuthSnapshot,
  readLatestAuthSnapshotSync,
  writeAuthCache,
  clearAuthCache,
  type CachedAuth,
} from '../lib/authCache'
import { invalidateSubjectsCache } from '../lib/subjectsCache'
import { clearBorrowOptIns } from '../lib/borrowedPool'
import {
  clearClaimedAnonymousContinuationAllowed,
  markClaimedAnonymousContinuationAllowed,
} from '../lib/claimedAnonymousContinuation'

const AUTH_BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export interface Student {
  id: string
  name: string
  email: string
  target_university: string
  active_subject: string
  is_admin: boolean
  role?: string                  // 'student' | 'teacher' | 'admin'
  teacher_subject_ids?: string[] // when role === 'teacher'
  onboarded: boolean
  phone_number: string | null
  whatsapp_opted_in: boolean
  avatar_url: string | null
  province?: string | null
  school?: string | null
  gender?: 'F' | 'M' | 'X' | null
  birthdate?: string | null   // ISO date "YYYY-MM-DD" or null
  is_current_grade_12?: boolean | null
  expected_grade_12_completion?: string | null   // ISO date "YYYY-MM-DD" or null
  grade_12_completed_year?: number | null        // year-only, set when is_current_grade_12 === false
  // Dual-mode (DUAL_MODE_NATIONAL_12_PLAN.md). preferred_mode drives the
  // /subjects toggle default, the question-pool filter, and which study-
  // path suggestions surface. national_12_track describes the student's
  // 12ª-classe orientation (consulted only in 12ª view per Q7).
  preferred_mode?:    'admission' | 'national_12' | 'both'
  national_12_track?: 'sciences'  | 'letters'     | 'mixed' | null
  // Re-prompt cadence — drives the recurring banner in SubjectsBanners.
  // The banner only fires when after_iso < now AND count < 3 (cap).
  grade_12_reprompt_after?: string | null
  grade_12_reprompt_count?: number
  // ── Onboarding v2 (derived by /auth/me) ──
  onboarding_style_v2?: boolean
  has_programme?: boolean
  on_placeholder?: boolean
  active_programme_id?: string | null
  days_since_signup?: number
  // Avaliação Inteligente Phase 1a — ISO timestamp of the most recent
  // completed PlacementSession (any subject), or null when the student
  // has never finished one. SubjectsBanners uses this to render the
  // "Repetir avaliação em X dias" cooldown on the CTA.
  last_placement_at?: string | null
  // Tier-driven discovery re-take cooldown, sourced from
  // SubscriptionTierFeature.repeat_discovery_days. NOT a hardcoded
  // frontend constant — admins edit this per-tier in Planos →
  // Funcionalidades. Semantics:
  //   0  → re-take anytime (no cooldown)
  //   >0 → wait this many days between completed runs
  //   -1 → re-take disabled (banner CTA permanently locked)
  repeat_discovery_days?: number
  max_programmes?: number | null       // null = unlimited (premium)
  max_universities?: number | null     // null = unlimited (premium)
  banners?: {
    whatsapp_dismissed_until:   string | null
    reevaluate_dismissed_until: string | null
  }
  nudge_config?: {
    profile_completion_dismiss_days?: number
    grade12_opt_in_dismiss_days?: number
    name_fix_dismiss_days?: number
    email_verify_dismiss_hours?: number
  }
  // In-app archive notification (2026-05-02). Non-null when an admin
  // archived a Programme/University the student was committed to AND
  // the student hasn't dismissed the banner yet. The frontend's
  // <ArchiveNoticeBanner> reads this and renders an amber banner;
  // POST /auth/dismiss-archive-notice clears it server-side, then
  // useAuth.refresh() picks up the cleared state.
  archived_programme_notice?: {
    kind:                'programme' | 'university'
    programme_name?:     string
    university_short_name?: string
    university_name?:    string
    archived_at:         string
  } | null
  // Course-update notice (PR follow-up, 2026-05-04). Informational
  // banner shown when admin updated seats or subject-weight on a
  // programme the student is committed to. Different urgency from
  // the archive notice — the student doesn't have to act, just be
  // informed. Cleared by POST /auth/dismiss-course-update-notice.
  course_update_notice?: {
    kind:                  'seats' | 'requirements'
    programme_name?:       string
    university_short_name?: string
    new_seats?:            number
    applicable_year?:      number
    weight_breakdown?:     Record<string, number>
    updated_at:            string
  } | null
  // Outcome of a question this student reported. 'resolved' = the question
  // was changed; 'dismissed' = reviewed and left as-is. `note` is the
  // teacher's optional message to the student. Unlike the two notices above,
  // dismissal writes a NudgeLog marker rather than a Student timestamp, so
  // there is no matching column in the schema. Cleared by
  // POST /auth/dismiss-flag-notice.
  flag_notice?: {
    outcome:            'resolved' | 'dismissed'
    flag_id:            string | null
    note:               string | null
    question_id:        string | null
    question_number:    number | null
    source_exam_year:   number | null
    source_institution: string | null
    source_paper:       string | null
    subject:            { code: string; name: string } | null
    resolved_at:        string
  } | null
  feedback_reply_notice?: {
    rating_id:   string | null
    note:        string | null
    replied_at:  string
  } | null
  // In-app rating prompt (2026-05-04). True when /auth/me's trigger
  // logic decided the modal should auto-open: 3+ day streak, no
  // Rating row yet, and not inside the 30-day dismiss cooldown.
  // Submit (POST /ratings) writes a Rating row, which makes this
  // permanently false. Dismiss (POST /ratings/dismiss) bumps the
  // cooldown so this returns to false until 30 days from now.
  should_show_rating_prompt?: boolean
  tutor_ai_disclosure_seen_at?: string | null
  topic_review_count?: number
  // Subscription state — denormalised onto the cached user so callers
  // don't re-fetch /auth/me just to derive `isSubscribed`. Source of
  // truth is /auth/me's `subscription` top-level field; we copy it
  // into Student here so the existing authCache write-through covers
  // it without schema changes. (Perf review Finding 1, 2026-05-05.)
  subscription?: { plan: string; status: string } | null
  is_anonymous?: boolean
  anon_questions_used?: number
  anon_tutor_chats_used?: number
}

interface AuthState {
  user: Student | null
  loading: boolean
  logout: () => Promise<void>
  refresh: () => Promise<Student | null>
}

const AuthContext = createContext<AuthState | null>(null)

function useProvideAuth(): AuthState {
  const [bootCachedAuth] = useState<CachedAuth | null>(() => readLatestAuthSnapshotSync())
  const [user,    setUserRaw] = useState<Student | null>(bootCachedAuth?.student ?? null)
  const [loading, setLoading] = useState(!bootCachedAuth)

  // Wrap setUser so every user change also updates Sentry + PostHog context.
  // Both only see the opaque student id + non-PII cohort dimensions — never
  // name, email, phone. When the user logs out (null), both are cleared.
  const setUser = useCallback((next: Student | null) => {
    setUserRaw(next)
    if (next && !next.is_anonymous) {
      clearAnonymousSession()
    }
    if (next?.has_programme) {
      clearClaimedAnonymousContinuationAllowed()
    }
    setSentryUser(next?.id ?? null)
    identifyClarity(next?.id ?? null)
    if (next) {
      identifyAnalytics(next.id, {
        role:                   next.role ?? 'student',
        is_admin:               next.is_admin,
        onboarding_style_v2:    next.onboarding_style_v2 ?? false,
        has_programme:          next.has_programme ?? false,
        active_programme_id:    next.active_programme_id ?? null,
        days_since_signup:      next.days_since_signup ?? 0,
      })
    } else {
      identifyAnalytics(null)
    }
  }, [])

  useEffect(() => {
    // Hydrate from cached /auth/me data first, then revalidate in the background.
    if (IS_FIREBASE_ENABLED && auth) {
      let cancelled = false
      if (!bootCachedAuth) {
        void readLatestAuthSnapshot().then((cached) => {
          if (cancelled || !cached) return
          setUser(cached.student)
          setLoading(false)
        })
      }

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          const anonymousToken = getAnonymousToken()
          if (anonymousToken) {
            try {
              const data = await api.get<{ student: Student; subscription?: { plan: string; status: string } | null }>('/auth/me')
              const merged = { ...data.student, subscription: data.subscription ?? null }
              setUser(merged)
            } catch (err: any) {
              if (err?.status === 401 || err?.status === 404) {
                clearAnonymousSession()
              }
              setUser(null)
            } finally {
              setLoading(false)
            }
            return
          }

          // Clear cached profile data when there is no authenticated user.
          await clearAuthCache().catch(() => {})
          setUser(null)
          setLoading(false)
          return
        }

        // Keep the anonymous carrier long enough to claim it into a new account.
        const anonymousSnapshot = getAnonymousSession()

        // A real Firebase session supersedes any stale anonymous carrier.
        clearAnonymousSession()

        // Hydrate immediately from cached /auth/me data when available.
        const cached = await readAuthCache(firebaseUser.uid)
        let hydratedFromCache = false
        if (cached) {
          setUser(cached.student)
          setLoading(false)
          hydratedFromCache = true
        }

        // Always revalidate against /auth/me after cache hydration.
        try {
          const data = await api.get<{ student: Student; subscription?: { plan: string; status: string } | null }>('/auth/me')
          const merged = { ...data.student, subscription: data.subscription ?? null }
          setUser(merged)
          await writeAuthCache({ firebase_uid: firebaseUser.uid, student: merged })
        } catch (err: any) {
          if (err instanceof OfflineError) {
            // Keep cached state when offline; otherwise fall back to LoginPage.
            if (!hydratedFromCache) {
              setUser(null)
            }
          } else if (err.status === 404 || err.status === 401) {
            // First social sign-in: Firebase user exists, but our Student row does not yet.
            // Claim anonymous work first when a carrier token exists.
            try {
              if (anonymousSnapshot) {
                const fbToken = await firebaseUser.getIdToken()
                const resp = await fetch(
                  `${AUTH_BASE_URL}/auth/claim-anonymous`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type':  'application/json',
                      'Authorization': `Bearer ${anonymousSnapshot.token}`,
                    },
                    body: JSON.stringify({
                      signup_kind:    'firebase',
                      firebase_token: fbToken,
                      name:           firebaseUser.displayName ?? undefined,
                    }),
                  },
                )
                let claimData: {
                  student:         Student
                  subscription?:   { plan: string; status: string } | null
                  claimed_summary?: { questions_answered: number; tutor_chats_used: number }
                }
                if (!resp.ok) {
                  // The popup may already have claimed the anonymous row
                  // explicitly. In that race, the background listener
                  // must converge on the now-valid /auth/me response
                  // instead of clearing the user and bouncing to /login.
                  if (resp.status === 409) {
                    const me = await api.get<{ student: Student; subscription?: { plan: string; status: string } | null }>('/auth/me')
                    claimData = {
                      student: me.student,
                      subscription: me.subscription ?? null,
                    }
                  } else {
                    throw new Error(`claim-anonymous status ${resp.status}`)
                  }
                } else {
                  claimData = await resp.json() as {
                    student:         Student
                    subscription?:   { plan: string; status: string } | null
                    claimed_summary: { questions_answered: number; tutor_chats_used: number }
                  }
                }
                const questionsKept = claimData.claimed_summary?.questions_answered ?? 0
                const merged = { ...claimData.student, subscription: claimData.subscription ?? null }
                // Mark the toast so /subjects can render the
                // "we kept N questions" acknowledgement once.
                try {
                  localStorage.setItem(
                    'acesso_just_claimed',
                    JSON.stringify({ questions_kept: questionsKept }),
                  )
                } catch { /* localStorage disabled — toast just won't fire */ }
                if (!merged.has_programme) {
                  markClaimedAnonymousContinuationAllowed()
                }
                setUser(merged)
                await writeAuthCache({ firebase_uid: firebaseUser.uid, student: merged })
                // anon_funnel.claim_success (Slice 12, 2026-05-19). The
                // conversion completion. We also call identifyAnalytics
                // with the authenticated student id — even though it's
                // the same row id we were already tracking against, the
                // identify call updates PostHog's user profile with the
                // post-claim cohort props (is_anonymous=false, etc.).
                // previous_id is the anon snapshot id; since both ids
                // are identical (same Student row), the property is
                // informational, used by funnels that want to count
                // the identity flip explicitly.
                identifyAnalytics(merged.id, {
                  is_anonymous:      false,
                  claimed_anonymous: true,
                  previous_id:       anonymousSnapshot.student_id,
                })
                track('anon_funnel.claim_success', {
                  signup_kind:    'firebase',
                  provider:       firebaseUser.providerData[0]?.providerId ?? 'unknown',
                  questions_kept: questionsKept,
                  student_id:     merged.id,
                })
                track('signup_completed', {
                  source:            'social',
                  provider:          firebaseUser.providerData[0]?.providerId ?? 'unknown',
                  claimed_anonymous: true,
                  questions_kept:    questionsKept,
                })
              } else {
                const data = await api.post<{ student: Student }>('/auth/social-register')
                setUser(data.student)
                await writeAuthCache({ firebase_uid: firebaseUser.uid, student: data.student })
                track('signup_completed', { source: 'social', provider: firebaseUser.providerData[0]?.providerId ?? 'unknown' })
                // Kick off onboarding so DailyCredit etc. are seeded
                await api.post('/auth/onboarding', { active_subject: 'MAT' }).catch(() => {})
              }
            } catch (registerErr: any) {
              // social-register / claim-anonymous failed. If THAT was
              // offline, prefer cached state when we have it; otherwise
              // sign out.
              if (registerErr instanceof OfflineError && hydratedFromCache) {
                // keep cached state
              } else {
                setUser(null)
              }
            }
          } else {
            // Other server errors (5xx etc.). Prefer cache when we have
            // it; only sign out when we don't.
            if (!hydratedFromCache) setUser(null)
          }
        }
        setLoading(false)
      })
      return () => {
        cancelled = true
        unsubscribe()
      }
    }

    // ── Demo mode ──────────────────────────────────────────────────────────
    const session = getSession()
    setUser((session?.student as Student) ?? null)
    setLoading(false)
  }, [])

  const logout = useCallback(async () => {
    // Dev-reset: if we're signing out of a demo allowlist account, wipe its
    // onboarding state server-side so the next login restarts from zero.
    // Silently no-ops in prod (server hard-gates on DEMO_MODE and allowlist).
    try {
      await api.post('/auth/dev-reset', {})
    } catch {
      /* expected outside demo mode — swallow */
    }

    // Per-user localStorage cleanup (Phase 5, 2026-06-12). The
    // subjects-cache lib persists snapshots keyed by the student id;
    // dropping them on logout prevents cross-account contamination on
    // shared devices. Captured BEFORE any signOut so we still have the
    // student id when the localStorage scan runs.
    const studentIdForCleanup = user?.id ?? null
    if (studentIdForCleanup) {
      try {
        invalidateSubjectsCache(studentIdForCleanup)
      } catch {
        // Best-effort — localStorage may be unavailable (private mode).
        // Stale rows linger but next sign-in writes its own keys.
      }
    }

    // UEM borrow consent, same reasoning one step further: the subjects cache
    // leaks stale DATA between accounts, this leaks a DECISION. A student who
    // never saw the prompt would otherwise inherit the previous account's
    // agreement to practise with another institution's questions, and the
    // server cannot tell the difference because it only checks that the pool
    // is empty. Cleared even when the id is missing, which also sweeps the
    // old unscoped key.
    clearBorrowOptIns(studentIdForCleanup)

    if (IS_FIREBASE_ENABLED && auth) {
      // Wipe the local /auth/me cache BEFORE signOut. We need the UID
      // to clear the right row, and signOut nulls auth.currentUser.
      // Best-effort: a failure here just means the row gets overwritten
      // on the next sign-in (cache is keyed by UID, so a different
      // account's row wouldn't be served anyway).
      const uid = auth.currentUser?.uid
      if (uid) await clearAuthCache(uid).catch(() => {})
      clearAnonymousSession()
      await auth.signOut()
    } else {
      clearSession()
    }
    setUser(null)
    window.location.href = '/login'
  }, [user?.id])

  /**
   * Re-fetch /auth/me and update the cached user. Called after mutations
   * that change onboarding-v2 state (programme commits, context switches,
   * banner dismissals, profile-contact saves).
   *
   * Offline-PWA PR (2026-05-04): write through to authCache on success
   * so post-mutation state survives the next cold-launch even if the
   * student's network drops between now and then.
   */
  const refresh = useCallback(async (): Promise<Student | null> => {
    try {
      const data = await api.get<{ student: Student; subscription?: { plan: string; status: string } | null }>('/auth/me')
      const merged = { ...data.student, subscription: data.subscription ?? null }
      setUser(merged)
      const uid = (IS_FIREBASE_ENABLED && auth?.currentUser?.uid) || null
      if (uid) await writeAuthCache({ firebase_uid: uid, student: merged })
      return merged
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 404) {
        // During anonymous→claimed upgrade in Firebase mode, a very
        // short-lived 401/404 from /auth/me can race ahead of the auth
        // listener's final settled state. Do not treat that as a real
        // logout while Firebase still has an authenticated user.
        if (!(IS_FIREBASE_ENABLED && auth?.currentUser)) {
          clearAnonymousSession()
          setUser(null)
        }
      }
      /* network hiccup — silently ignore; caller will see stale but non-broken state */
      return null
    }
  }, [])

  return { user, loading, logout, refresh }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useProvideAuth()
  return createElement(AuthContext.Provider, { value: auth }, children)
}

export function useAuth(): AuthState {
  const auth = useContext(AuthContext)
  if (!auth) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return auth
}
